import React, { useState, useEffect } from "react";
import { NavLink, Link, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useStaff, roleLabel } from "@/context/StaffContext";
import staffApi from "@/lib/staffApi";
import { MONOGRAM } from "@/data/site";
import { LayoutDashboard, Boxes, ShoppingCart, Package, Users, ShieldCheck, ScrollText, LogOut, MapPin, Store, Activity, Settings as SettingsIcon, Mail, MailCheck } from "lucide-react";

const NAV = [
  { to: "/staff/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/staff/dashboard/inventory", label: "Inventory", icon: Boxes },
  { to: "/staff/dashboard/products", label: "Products", icon: Package },
  { to: "/staff/dashboard/orders", label: "Orders", icon: ShoppingCart },
  { to: "/staff/dashboard/members", label: "Members", icon: Users },
  { to: "/staff/dashboard/waitlist", label: "Waitlist", icon: Mail },
  { to: "/staff/dashboard/activity", label: "Staff Activity", icon: Activity },
  { to: "/staff/dashboard/email-log", label: "Email Log", icon: MailCheck, role: "admin" },
  { to: "/staff/dashboard/locations", label: "Locations", icon: MapPin, role: "super" },
  { to: "/staff/dashboard/staff", label: "Staff", icon: ShieldCheck, role: "super" },
  { to: "/staff/dashboard/settings", label: "Settings", icon: SettingsIcon, role: "super" },
  { to: "/staff/dashboard/audit", label: "Audit Log", icon: ScrollText },
];

export default function DashboardLayout() {
  const { staff, logout, can } = useStaff();
  const navigate = useNavigate();
  const [lowCount, setLowCount] = useState(0);

  useEffect(() => {
    if (staff) staffApi.get("/admin/overview").then((r) => setLowCount(r.data.low_stock_count || 0)).catch(() => {});
  }, [staff]);

  if (staff === null) return <div className="flex min-h-screen items-center justify-center bg-black text-cream/50">Loading…</div>;
  if (staff === false) return <Navigate to="/staff/login" replace />;

  return (
    <div className="min-h-screen bg-black lg:grid lg:grid-cols-[240px_1fr]" data-testid="dashboard-layout">
      <aside className="border-r border-white/10 bg-ink lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col">
        <div className="flex items-center gap-3 border-b border-white/10 px-6 py-5 lg:shrink-0">
          <img src={MONOGRAM} alt="HYEAMAN" className="h-8 w-auto" />
          <div><p className="font-serif text-sm tracking-widest text-cream">HYEAMAN</p><p className="text-[0.6rem] uppercase tracking-widest text-gold">Operations</p></div>
        </div>
        <Link to="/" data-testid="view-store-link" className="flex items-center gap-2 border-b border-white/10 px-6 py-3 text-xs uppercase tracking-widest text-cream/70 transition hover:bg-white/5 hover:text-gold lg:shrink-0">
          <Store size={15} /> View Store
        </Link>
        <nav className="flex gap-1 overflow-x-auto p-3 lg:min-h-0 lg:flex-1 lg:flex-col lg:overflow-y-auto" data-testid="dashboard-nav">
          {NAV.filter((n) => !n.role || can(n.role)).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `flex shrink-0 items-center gap-3 px-4 py-3 text-xs uppercase tracking-widest transition ${isActive ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-cream/60 hover:text-cream hover:bg-white/5"}`}>
              <n.icon size={16} /> {n.label}
              {n.label === "Inventory" && lowCount > 0 && (
                <span className="ml-1 flex h-5 min-w-5 items-center justify-center bg-yellow-500 px-1 text-[0.6rem] font-bold text-black" data-testid="inventory-low-badge">{lowCount}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4 lg:mt-auto lg:shrink-0">
          <p className="text-sm text-cream">{staff.name}</p>
          <p className="mb-3 text-[0.6rem] uppercase tracking-widest text-gold">{roleLabel[staff.role]}</p>
          <button onClick={() => { logout(); navigate("/staff/login"); }} data-testid="staff-logout" className="flex items-center gap-2 text-xs uppercase tracking-widest text-cream/50 hover:text-gold"><LogOut size={14} /> Sign out</button>
        </div>
      </aside>
      <div className="p-4 sm:p-8"><Outlet /></div>
    </div>
  );
}

export function PageHead({ kicker, title, action }) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div><p className="overline-label mb-1">{kicker}</p><h1 className="font-black uppercase tracking-tight text-cream text-2xl sm:text-3xl">{title}</h1></div>
      {action}
    </div>
  );
}
